import React, { useEffect, useRef } from 'react';
import { Terminal, Database, Zap } from 'lucide-react';
export interface SlashCommand {
  command: string;
  description: string;
  category: string;
  service: 'confluence' | 'jira' | 'system';
}
interface SlashCommandPaletteProps {
  commands: SlashCommand[];
  selectedIndex: number;
  onSelect: (command: string) => void;
}
export function SlashCommandPalette({
  commands,
  selectedIndex,
  onSelect
}: SlashCommandPaletteProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  // Auto-scroll to selected item
  useEffect(() => {
    if (selectedRef.current && containerRef.current) {
      const container = containerRef.current;
      const element = selectedRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      if (elementRect.bottom > containerRect.bottom) {
        container.scrollTop += elementRect.bottom - containerRect.bottom;
      } else if (elementRect.top < containerRect.top) {
        container.scrollTop -= containerRect.top - elementRect.top;
      }
    }
  }, [selectedIndex]);
  if (commands.length === 0) {
    return <div className="absolute bottom-full left-0 mb-2 w-full bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 p-3 text-sm text-slate-400 text-center animate-in fade-in slide-in-from-bottom-2" data-id="element-2672">
        No se encontraron comandos
      </div>;
  }
  // Group commands by category
  const grouped = commands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {} as Record<string, SlashCommand[]>);
  let globalIndex = 0;
  return <div ref={containerRef} className="absolute bottom-full left-0 mb-2 w-full max-h-72 overflow-y-auto bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 py-2 animate-in fade-in slide-in-from-bottom-2 z-50 custom-scrollbar" data-id="element-2673">
      {Object.entries(grouped).map(([category, cmds]) => <div key={category} className="mb-2 last:mb-0" data-id="element-2674">
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider z-10 flex items-center gap-1.5" data-id="element-2675">
            {category === 'Confluence' && <Database size={12} data-id="element-2676" />}
            {category === 'Jira' && <Terminal size={12} data-id="element-2677" />}
            {category === 'System' && <Zap size={12} data-id="element-2678" />}
            {category}
          </div>
          <div className="px-1.5" data-id="element-2679">
            {cmds.map(cmd => {
          const isSelected = globalIndex === selectedIndex;
          const currentIndex = globalIndex++;
          return <button key={cmd.command} ref={isSelected ? selectedRef : null} onClick={() => onSelect(cmd.command)} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${isSelected ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`} data-id="element-2680">
                  <div className="flex items-center gap-2 overflow-hidden" data-id="element-2681">
                    <span className={`font-mono text-sm font-semibold ${isSelected ? 'text-white' : 'text-indigo-400'}`} data-id="element-2682">
                      {cmd.command}
                    </span>
                    <span className={`text-xs truncate ${isSelected ? 'text-indigo-100' : 'text-slate-500'}`} data-id="element-2683">
                      {cmd.description}
                    </span>
                  </div>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wide shrink-0 ml-2 ${isSelected ? 'bg-indigo-500 text-white' : 'bg-slate-800 text-slate-500'}`} data-id="element-2684">
                    {cmd.service}
                  </span>
                </button>;
        })}
          </div>
        </div>)}
    </div>;
}